import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormDropzone from "../../shared/dropzone";
import { multipartFormData } from "../../helpers/api.services";
import { CompanyService } from "./service";
import { LocationService } from "../location/service"; // Import LocationService
import { UserService } from "../user/service"; // Import UserService
import { COMPANY_URL } from "../../helpers/api.routes";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Editor } from "@tinymce/tinymce-react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { JobTargetGroupsService } from "../jobTargetGroups/service";
import { JobTypeService } from "../jobType/service";
import { ISelect } from "../interfaces";
import { JobCategoriesService } from "../jobCategories/service";
import { AuthContext } from "../../context/AuthContext";
import { EnumsContext } from "../../context/EnumsContext";

const schema = yup.object().shape({
  name: yup.string().required("formErrors.nameRequired"),
  contact: yup.object().shape({
    phone: yup.string(), // Optional - not required in frontend
    city: yup.string().required("formErrors.cityRequired"),
    zip: yup.string(), // Optional - not required in frontend
    address: yup.string().required("formErrors.addressRequired"),
    addressSuffix: yup.string(), // Optional - not required in frontend
  }),
  email: yup
    .string()
    .email("formErrors.invalidEmail")
    .required("formErrors.emailRequired"),
  website: yup
    .string()
    .url("formErrors.invalidUrl")
    .required("formErrors.websiteRequired"),
  socialMedia: yup.object().shape({
    facebook: yup.string().url("formErrors.invalidUrl"),
    twitter: yup.string().url("formErrors.invalidUrl"),
    instagram: yup.string().url("formErrors.invalidUrl"),
    linkedin: yup.string().url("formErrors.invalidUrl"),
  }),
  about: yup.string().required("formErrors.aboutRequired"), // Now required to match frontend
  foundedIn: yup
    .number()
    .typeError("formErrors.invalidYear")
    .positive("formErrors.invalidYear")
    .integer("Invalid year"),
  logo: yup.mixed(),
  targetGroups: yup
    .array()
    .of(yup.number().required())
    .min(1, "formErrors.targetGroupRequired")
    .required("formErrors.fieldRequired"),
  categories: yup
    .array()
    .of(yup.number().required())
    .min(1, "formErrors.categoryRequired")
    .required("formErrors.fieldRequired"),
  locations: yup
    .array()
    .of(yup.number())
    .optional(), // Made optional - no longer mandatory
  mainContact: yup.number().required("formErrors.mainContactRequired"), // Required in both modes
});

interface CompanyState {
  name: string;
  contact: {
    phone?: string;
    city: string;
    zip?: string;
    address: string;
    addressSuffix?: string;
  };
  email: string;
  website: string;
  socialMedia: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
  about: string; // Required to match frontend validation
  foundedIn?: number;
  logo?: any;

  locations?: any[]; // Selected location IDs - now optional
  categories: number[]; // Selected category IDs
  targetGroups: number[]; // Selected target group IDs
  mainContact: number; // Required field to match frontend validation
}

const CompanyForm: React.FC = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { user, logout } = auth;
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<CompanyState>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      contact: {
        phone: "",
        city: "",
        zip: "",
        address: "",
        addressSuffix: "",
      },
      email: "",
      website: "",
      socialMedia: {
        facebook: "",
        twitter: "",
        instagram: "",
        linkedin: "",
      },
      about: "", // Required field
      foundedIn: undefined,
      logo: undefined,
      locations: [],
      categories: [],
      targetGroups: [],
      mainContact: user?.id, // Set to current user ID as default for required number field
    },
  });

  const [locationList, setLocationList] = useState<any[]>([]); // State for location options
  const [locationTree, setLocationTree] = useState<any[]>([]); // Tree structure for locations
  const [checkedLocations, setCheckedLocations] = useState<string[]>([]); // Checked nodes
  const [expandedLocations, setExpandedLocations] = useState<string[]>([]); // Expanded nodes
  const [targetGroupList, setTargetGroupList] = useState<ISelect[]>([]);
  const [secondDropdownOptions, setSecondDropdownOptions] = useState<any[]>([]);
  const [mainCategoryList, setMainCategoryList] = useState<any[]>([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<
    number | null
  >(null);
  const [companyUsers, setCompanyUsers] = useState<ISelect[]>([]); // State for company users
  const [checkedCategories, setCheckedCategories] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const handleLogoUpload = (files: any) => {
    setValue("logo", files[0]);
  };

  const { enums } = useContext(EnumsContext);

  const jobLocationType = enums.jobLocationType?.map((item: any) => ({
    label: item[0].toUpperCase() + item.slice(1),
    value: item,
  }));

  const onSubmit = async (data: CompanyState) => {
    const url = isEdit ? `${COMPANY_URL}/${id}` : COMPANY_URL;
    const method = isEdit ? "put" : "post";

    const formData = {
      ...data,
      locations: checkedLocations.map((id) => parseInt(id)), // Convert checked IDs to numbers
      categories: data.categories || [], // Ensure categories are included
    };

    multipartFormData(data.logo, url, JSON.stringify(formData), method, "logo")
      .then(() => {
        SuccessToast(t(`company.${isEdit ? "update" : "create"}`));
        navigate("/company");
        reset();
      })
      .catch((error) => ErrorToast(error));
  };

  useEffect(() => {
    if (selectedMainCategory) {
      const mainCategory = mainCategoryList.find(
        (cat) => cat.value === selectedMainCategory
      );
      if (mainCategory && mainCategory.children) {
        const options = mainCategory.children.flatMap((secondLevel: any) => [
          {
            label: (
              <span style={{ color: "#aaaaaa", fontWeight: "bold" }}>
                {secondLevel.title}
              </span>
            ),
            value: secondLevel.id,
            isDisabled: true,
          },
          ...(secondLevel.children || []).map((thirdLevel: any) => ({
            label: thirdLevel.title,
            value: thirdLevel.id,
          })),
        ]);
        setSecondDropdownOptions(options);
      } else {
        setSecondDropdownOptions([]);
      }
    } else {
      setSecondDropdownOptions([]);
    }
  }, [selectedMainCategory, mainCategoryList]);

  useEffect(() => {
    if (isEdit) {
      const fetchCompany = async () => {
        try {
          const response = await CompanyService.getSingle(+id!);
          const company = response;

          reset({
            name: company.name,
            contact: {
              phone: company.contact.phone,
              city: company.contact.city,
              zip: company.contact.zip,
              address: company.contact.address,
              addressSuffix: company.contact.addressSuffix, // Prepopulate addressSuffix
            },
            email: company.email,
            website: company.website || "",
            socialMedia: {
              facebook: company.socialMedia.facebook || "",
              twitter: company.socialMedia.twitter || "",
              instagram: company.socialMedia.instagram || "",
              linkedin: company.socialMedia.linkedin || "",
            },
            about: company.about,
            foundedIn: company.foundedIn || undefined,
            logo: company.logo,
            locations: company.locations.map((loc: any) => loc.id),
            targetGroups: company?.targetGroups?.map((tg: any) => tg.id) || [],
            categories: company?.jobCategories?.map((cat: any) => cat.id) || [],
            mainContact: company?.mainContact?.id || user?.id,
          });
          // Set selectedMainCategory
          if (company.jobCategories.length > 0) {
            const mainCategory =
              company.jobCategories[0].parent?.parent ||
              company.jobCategories[0].parent;
            if (mainCategory) {
              setSelectedMainCategory(mainCategory.id);

              // Safely handle undefined children
              const secondLevelOptions = (mainCategory.children || []).flatMap(
                (secondLevel: any) => [
                  {
                    label: (
                      <span style={{ color: "#aaaaaa", fontWeight: "bold" }}>
                        {secondLevel.title}
                      </span>
                    ),
                    value: secondLevel.id,
                    isDisabled: true,
                  },
                  ...(secondLevel.children || []).map((thirdLevel: any) => ({
                    label: thirdLevel.title,
                    value: thirdLevel.id,
                  })),
                ]
              );

              setSecondDropdownOptions(secondLevelOptions);
            } else {
              setSecondDropdownOptions([]); // Clear options if no main category is found
            }
          } else {
            setSecondDropdownOptions([]); // Clear options if no job categories are present
          }
          // Update editorState with the about content
          // Preselect locations in the CheckboxTree
          const selectedLocationIds = company.locations.map((loc: any) =>
            loc.id.toString()
          );
          setCheckedLocations(selectedLocationIds);

          // Expand parent nodes for the selected locations
          const findParentNodes = (node: any, expanded: string[]): string[] => {
            if (node.parent) {
              expanded.push(node.parent.id.toString());
              return findParentNodes(node.parent, expanded);
            }
            return expanded;
          };

          let expandedNodes: string[] = [];
          company.locations.forEach((loc: any) => {
            expandedNodes = [...expandedNodes, ...findParentNodes(loc, [])];
          });

          setExpandedLocations(Array.from(new Set(expandedNodes))); // Remove duplicates
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchCompany();
    }
  }, [isEdit, id, reset]);

  useEffect(() => {
    // Fetch location options
    LocationService.getAll({ perPage: 0, page: 1 })
      .then((res) => {
        setLocationList(
          res.data.map((loc: any) => ({ label: loc.city, value: loc.id }))
        );
      })
      .catch((err) => ErrorToast(err));
  }, []);

  useEffect(() => {
    JobCategoriesService.getAll()
      .then((res) => {
        const processCategories = (categories: any[], level = 1): any[] => {
          if (level > 3) return []; // Stop processing beyond level 3
          return categories.map((category) => ({
            value: category.id.toString(),
            label: category.title || t("category.unnamed"),
            children: processCategories(category.children || [], level + 1),
          }));
        };
        setMainCategoryList(processCategories(res));
      })
      .catch((err) => ErrorToast(err));

    JobTargetGroupsService.getAll({ perPage: 0, page: 1 })
      .then((res) => {
        setTargetGroupList(
          res.data.map((tg: any) => ({ label: tg.title, value: tg.id }))
        );
      })
      .catch((err) => ErrorToast(err));

    // Fetch locations and build the tree structure
    LocationService.getTree({ term: "", parent: null })
      .then((res) => {
        const processLocations = (locations: any[], level = 1): any[] => {
          if (level > 3) return []; // Stop processing beyond level 3
          return locations.map((location) => ({
            value: location.id.toString(),
            label: location.name || t("location.unnamed"),
            icon:
              level === 3 ? (
                <i className="fa fa-map-marker-alt text-primary"></i>
              ) : undefined, // Custom icon for level 3
            children: processLocations(location.children || [], level + 1),
          }));
        };
        setLocationTree(processLocations(res));
      })
      .catch((err) => ErrorToast(err));
  }, [t]);

  useEffect(() => {
    if (isEdit) {
      // Fetch company users during editing
      UserService.getAll({ company: id })
        .then((res) => {
          setCompanyUsers(
            res.data.map((user: any) => ({
              label: user.firstName + " " + user.lastName,
              value: user.id,
            }))
          );
        })
        .catch((err) => ErrorToast(err));
    }
  }, [isEdit, id, setValue]);

  useEffect(() => {
    if (isEdit && mainCategoryList.length > 0) {
      const fetchCompanyCategories = async () => {
        try {
          const response = await CompanyService.getSingle(+id!);
          const company = response;

          // Preselect categories in the CheckboxTree
          const selectedCategoryIds =
            company?.jobCategories?.map((cat: any) => cat.id.toString()) || [];
          setCheckedCategories(selectedCategoryIds);

          // Expand parent nodes for the selected categories
          const findParentNodes = (
            categories: any[],
            selected: string[]
          ): string[] => {
            const expanded: string[] = [];
            const traverse = (nodes: any[], parents: string[] = []) => {
              nodes.forEach((node) => {
                if (selected.includes(node.value)) {
                  expanded.push(...parents);
                }
                if (node.children) {
                  traverse(node.children, [...parents, node.value]);
                }
              });
            };
            traverse(categories);
            return Array.from(new Set(expanded)); // Remove duplicates
          };

          const expandedCategoryIds = findParentNodes(
            mainCategoryList,
            selectedCategoryIds
          );
          setExpandedCategories(expandedCategoryIds);
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchCompanyCategories();
    }
  }, [isEdit, id, mainCategoryList]);

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.name")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.name")}
                            {...field}
                          />
                        )}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">
                          {t(errors.name.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  {/* Location Field */}

                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">{t("company.phone")}</label>
                      <Controller
                        name="contact.phone"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.contact?.phone ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.phone")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.phone && (
                        <div className="invalid-feedback">
                          {t(errors.contact?.phone.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.city")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="contact.city"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.contact?.city ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.city")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.city && (
                        <div className="invalid-feedback">
                          {t(errors.contact?.city.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.addressSuffix")}
                      </label>
                      <Controller
                        name="contact.addressSuffix"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.contact?.addressSuffix ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.addressSuffix")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.addressSuffix && (
                        <div className="invalid-feedback">
                          {t(errors.contact.addressSuffix.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">{t("company.zip")}</label>
                      <Controller
                        name="contact.zip"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.contact?.zip ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.zip")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.zip && (
                        <div className="invalid-feedback">
                          {t(errors.contact?.zip.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.address")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="contact.address"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.contact?.address ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.address")}
                            {...field}
                          />
                        )}
                      />
                      {errors.contact?.address && (
                        <div className="invalid-feedback">
                          {t(errors.contact?.address.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.email")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="email"
                            className={`form-control ${errors.email ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.email")}
                            {...field}
                          />
                        )}
                      />
                      {errors.email && (
                        <div className="invalid-feedback">
                          {t(errors.email.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.website")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="website"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.website ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.website")}
                            {...field}
                          />
                        )}
                      />
                      {errors.website && (
                        <div className="invalid-feedback">
                          {t(errors.website.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.facebook")}
                      </label>
                      <Controller
                        name="socialMedia.facebook"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.socialMedia?.facebook ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.facebook")}
                            {...field}
                          />
                        )}
                      />
                      {errors.socialMedia?.facebook && (
                        <div className="invalid-feedback">
                          {t(errors.socialMedia.facebook.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.twitter")}
                      </label>
                      <Controller
                        name="socialMedia.twitter"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.socialMedia?.twitter ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.twitter")}
                            {...field}
                          />
                        )}
                      />
                      {errors.socialMedia?.twitter && (
                        <div className="invalid-feedback">
                          {t(errors.socialMedia.twitter.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.instagram")}
                      </label>
                      <Controller
                        name="socialMedia.instagram"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.socialMedia?.instagram ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.instagram")}
                            {...field}
                          />
                        )}
                      />
                      {errors.socialMedia?.instagram && (
                        <div className="invalid-feedback">
                          {t(errors.socialMedia.instagram.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.linkedin")}
                      </label>
                      <Controller
                        name="socialMedia.linkedin"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.socialMedia?.linkedin ? "is-invalid" : ""
                              }`}
                            placeholder={t("company.linkedin")}
                            {...field}
                          />
                        )}
                      />
                      {errors.socialMedia?.linkedin && (
                        <div className="invalid-feedback">
                          {t(errors.socialMedia.linkedin.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  {/* Main Contact Field */}
                  <Col sm={6} xl={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.mainContact")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="mainContact"
                        control={control}
                        render={({ field }) => {
                          // In create mode, show current user as an option
                          // In edit mode, ensure current user is included in options if not already there
                          let options = isEdit ? companyUsers : [];

                          if (!isEdit && user) {
                            // Create mode: only show current user
                            options = [
                              {
                                label: user.firstName + " " + user.lastName,
                                value: user.id,
                              },
                            ];
                          } else if (isEdit && user) {
                            // Edit mode: ensure current user is in the options if not already there
                            const currentUserExists = companyUsers.some(
                              (u) => u.value === user.id
                            );
                            if (!currentUserExists) {
                              options = [
                                ...companyUsers,
                                {
                                  label: user.firstName + " " + user.lastName,
                                  value: user.id,
                                },
                              ];
                            }
                          }

                          return (
                            <Select
                              id="mainContact"
                              classNamePrefix="react-select"
                              className={`react-select-container ${errors.mainContact ? "is-invalid" : ""
                                }`}
                              value={options.find(
                                (userOption) => userOption.value === field.value
                              )}
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : null
                                )
                              }
                              options={options}
                              isDisabled={!isEdit} // Disable in create mode, enable in edit mode
                              isClearable={isEdit} // Only allow clearing in edit mode
                            />
                          );
                        }}
                      />
                      {errors.mainContact && (
                        <div className="invalid-feedback">
                          {t(errors.mainContact.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6} xl={3}>
                    <div className="mb-0 d-flex flex-column">
                      <label className="form-label">
                        {t("company.foundedIn")}
                      </label>
                      <Controller
                        name="foundedIn"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            selected={
                              field.value ? new Date(field.value, 0) : null
                            }
                            onChange={(date: Date | null) =>
                              field.onChange(date ? date.getFullYear() : null)
                            }
                            showYearPicker
                            dateFormat="yyyy"
                            className={`form-control ${errors.foundedIn ? "is-invalid" : ""
                              }`}
                            placeholderText={t("company.foundedIn")}
                          />
                        )}
                      />
                      {errors.foundedIn && (
                        <div className="invalid-feedback">
                          {t(errors.foundedIn.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  {/* Multi-Select Target Groups */}
                  <Col sm={3}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.targetGroups")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="targetGroups"
                        control={control}
                        render={({ field }) => (
                          <Select
                            isMulti
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.targetGroups ? "is-invalid" : ""
                              }`}
                            value={field.value.map((id: number) =>
                              targetGroupList.find(
                                (group) => group.value === id
                              )
                            )}
                            onChange={(selectedOptions) =>
                              field.onChange(
                                selectedOptions.map((option) => option?.value)
                              )
                            }
                            options={targetGroupList}
                          />
                        )}
                      />
                      {errors.targetGroups && (
                        <div className="invalid-feedback d-block">
                          {t(errors.targetGroups.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("company.mission")}{" "}
                        <span className="text-danger">*</span>
                      </label>{" "}
                      {/* Changed label */}
                      <Controller
                        name="about"
                        control={control}
                        render={({ field }) => (
                          <Editor
                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                            value={field.value}
                            onEditorChange={(content: string) => field.onChange(content)}
                            init={{
                              height: 360,
                              menubar: false,
                              branding: false,
                              plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'help', 'wordcount',
                              ],
                              toolbar:
                                'undo redo | blocks | bold italic underline strikethrough | ' +
                                'alignleft aligncenter alignright alignjustify | ' +
                                'bullist numlist outdent indent | table | link image | removeformat | code',
                              content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
                            }}
                          />
                        )}
                      />
                      {errors.about && (
                        <div className="invalid-feedback">
                          {t(errors.about.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Categories and Locations Side by Side */}
                  <Row className="mt-4">
                    {/* Multi-Select Categories */}
                    <Col sm={6}>
                      <label className="form-label">
                        {t("company.categories")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <div
                        className={`rounded p-3 ${errors.categories ? "is-invalid" : ""
                          }`}
                        style={{
                          minWidth: "300px",
                          backgroundColor: "var(--bs-body-bg)",
                          border: `1px solid ${errors.categories
                            ? "#dc3545"
                            : "var(--bs-border-color)"
                            }`,
                        }}
                      >
                        <CheckboxTree
                          nodes={mainCategoryList}
                          checked={checkedCategories}
                          expanded={expandedCategories}
                          onCheck={(checked) => {
                            setCheckedCategories(checked); // Update checked categories
                            setValue(
                              "categories",
                              checked.map((id) => parseInt(id))
                            ); // Update form state
                            clearErrors("categories"); // Clear the error immediately
                          }}
                          onExpand={setExpandedCategories}
                          iconsClass="fa5"
                        />
                        {errors.categories && (
                          <div className="invalid-feedback d-block">
                            {t(errors.categories.message as string)}
                          </div>
                        )}
                      </div>
                    </Col>

                    {/* Location Tree */}
                    <Col sm={6}>
                      <label className="form-label">
                        {t("company.locations")}
                      </label>
                      <div
                        className={`rounded p-3 ${errors.locations ? "is-invalid" : ""
                          }`}
                        style={{
                          minWidth: "300px",
                          backgroundColor: "var(--bs-body-bg)",
                          border: `1px solid ${errors.locations
                            ? "#dc3545"
                            : "var(--bs-border-color)"
                            }`,
                        }}
                      >
                        <CheckboxTree
                          nodes={locationTree}
                          checked={checkedLocations}
                          expanded={expandedLocations}
                          onCheck={(checked) => {
                            // Filter only level 3 nodes
                            const level3Checked = checked.filter((id) => {
                              const findNodeLevel = (
                                nodes: any[],
                                nodeId: string,
                                level = 1
                              ): boolean => {
                                for (const node of nodes) {
                                  if (node.value === nodeId) {
                                    return level === 3; // Return true if the node is level 3
                                  }
                                  if (node.children) {
                                    if (
                                      findNodeLevel(
                                        node.children,
                                        nodeId,
                                        level + 1
                                      )
                                    ) {
                                      return true;
                                    }
                                  }
                                }
                                return false;
                              };
                              return findNodeLevel(locationTree, id);
                            });

                            setCheckedLocations(level3Checked); // Update checked locations with only level 3 nodes
                            setValue(
                              "locations",
                              level3Checked.map((id) => parseInt(id))
                            ); // Update form state
                            clearErrors("locations"); // Clear the error immediately
                          }}
                          onExpand={setExpandedLocations}
                          iconsClass="fa5"
                        />
                        {errors.locations && (
                          <div className="invalid-feedback d-block">
                            {t(errors.locations.message as string)}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Row>
                <Col sm={6} className="my-3">
                  <FormDropzone
                    onFileUpload={handleLogoUpload}
                    existingFiles={getValues("logo") ? [getValues("logo")] : []}
                    accept="image/*" // Accept only image files
                  />
                  {errors.logo && (
                    <div className="invalid-feedback d-block">
                      {t("formErrors.logoRequired")}
                    </div>
                  )}
                </Col>
                <Col sm={12}>
                  <Button type="submit" variant="primary">
                    {t(`company.${isEdit ? "update" : "create"}`)}
                  </Button>
                </Col>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default CompanyForm;
